import React, { useState, useEffect, useRef, useCallback } from 'react';
import './QRScanner.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQrcode,
  faKey,
  faLock,
  faCamera,
  faImage,
  faCheck,
  faXmark,
  faSpinner,
  faArrowRight,
  faRefresh,
  faUser,
  faEnvelope,
  faPhone,
  faIdCard,
  faBuilding,
  faTicket,
  faExclamationTriangle,
  faCameraRotate,
} from '@fortawesome/free-solid-svg-icons';
import { Html5Qrcode } from 'html5-qrcode';

const API_BASE = process.env.REACT_APP_API_URL || 'https://khamma-ghani-server.vercel.app';

export default function QRScanner() {
  /* ── Auth State ── */
  const [apiKey, setApiKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  /* ── Scanner State ── */
  const [scanMode, setScanMode] = useState('camera'); // 'camera' | 'upload'
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState('');
  const [cameraId, setCameraId] = useState(null);
  const [cameras, setCameras] = useState([]);

  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastScannedRef = useRef('');
  const resultTimeoutRef = useRef(null);

  /* ── Check stored API key on mount ── */
  useEffect(() => {
    const storedKey = sessionStorage.getItem('rca_scan_key');
    if (storedKey) {
      verifyApiKey(storedKey, true);
    }
  }, []);

  /* ── Cleanup scanner on unmount ── */
  useEffect(() => {
    return () => {
      stopScanner();
      if (resultTimeoutRef.current) {
        clearTimeout(resultTimeoutRef.current);
      }
    };
  }, []);

  /* ── Verify API Key ── */
  const verifyApiKey = async (key, silent = false) => {
    if (!silent) setAuthLoading(true);
    setAuthError('');
    try {
      const res = await fetch(`${API_BASE}/api/verify-api-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: key }),
      });
      const data = await res.json();
      if (data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('rca_scan_key', key);
      } else {
        setAuthError(data.message || 'Invalid API key');
        sessionStorage.removeItem('rca_scan_key');
      }
    } catch (err) {
      setAuthError('Network error. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAuthSubmit = (e) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      setAuthError('Please enter an API key');
      return;
    }
    verifyApiKey(apiKey.trim());
  };

  /* ── Get Available Cameras ── */
  const getCameras = async () => {
    try {
      const devices = await Html5Qrcode.getCameras();
      setCameras(devices);
      if (devices.length > 0) {
        // Prefer back camera
        const backCam = devices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment')
        );
        setCameraId(backCam?.id || devices[0].id);
      }
      return devices;
    } catch (err) {
      setScanError('Unable to access cameras. Please check permissions.');
      return [];
    }
  };

  /* ── Start Camera Scanner ── */
  const startScanner = async () => {
    setScanError('');
    setScanResult(null);

    let camList = cameras;
    if (cameras.length === 0) {
      camList = await getCameras();
      if (camList.length === 0) return;
    }

    const selectedCam = cameraId || camList[0]?.id;
    if (!selectedCam) {
      setScanError('No camera found');
      return;
    }

    try {
      html5QrCodeRef.current = new Html5Qrcode('qr-reader');
      await html5QrCodeRef.current.start(
        selectedCam,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        onScanSuccess,
        () => {} // Ignore scan failures
      );
      setScanning(true);
    } catch (err) {
      setScanError('Failed to start camera: ' + err.message);
    }
  };

  /* ── Stop Camera Scanner ── */
  const stopScanner = async () => {
    if (html5QrCodeRef.current && scanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.log('Stop error:', err);
      }
    }
    setScanning(false);
  };

  /* ── Switch Camera ── */
  const switchCamera = async () => {
    if (cameras.length <= 1) return;
    const currentIdx = cameras.findIndex(c => c.id === cameraId);
    const nextIdx = (currentIdx + 1) % cameras.length;
    setCameraId(cameras[nextIdx].id);
    
    if (scanning) {
      await stopScanner();
      setTimeout(() => {
        startScanner();
      }, 300);
    }
  };

  /* ── Handle QR Scan Success ── */
  const onScanSuccess = useCallback(async (decodedText) => {
    // Prevent multiple scans of the same QR code within 5 seconds
    if (scanLoading || decodedText === lastScannedRef.current) return;
    
    lastScannedRef.current = decodedText;
    
    // Clear previous timeout
    if (resultTimeoutRef.current) {
      clearTimeout(resultTimeoutRef.current);
    }
    
    // Process the QR code (don't stop scanner for continuous scanning)
    processQRCode(decodedText);
    
    // Reset last scanned after 5 seconds to allow re-scan
    setTimeout(() => {
      lastScannedRef.current = '';
    }, 5000);
  }, [scanLoading]);

  /* ── Handle Image Upload ── */
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setScanError('');
    setScanResult(null);
    setScanLoading(true);

    try {
      if (!html5QrCodeRef.current) {
        html5QrCodeRef.current = new Html5Qrcode('qr-reader-upload');
      }
      const result = await html5QrCodeRef.current.scanFile(file, true);
      processQRCode(result);
    } catch (err) {
      setScanError('Could not read QR code from image. Please try again.');
      setScanLoading(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /* ── Process QR Code ── */
  const processQRCode = async (qrCode) => {
    setScanLoading(true);
    setScanError('');

    try {
      const res = await fetch(`${API_BASE}/api/registration/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_code: qrCode }),
      });
      const data = await res.json();

      if (data.success) {
        setScanResult({ type: 'success', data: data.registration, message: data.message });
      } else {
        setScanResult({ 
          type: 'error', 
          message: data.error || 'Invalid QR code',
          data: data.registration || null 
        });
      }
      
      // Auto-dismiss result after 4 seconds for continuous scanning
      resultTimeoutRef.current = setTimeout(() => {
        setScanResult(null);
      }, 4000);
      
    } catch (err) {
      setScanError('Network error. Please try again.');
    } finally {
      setScanLoading(false);
    }
  };

  /* ── Reset Scanner (manual dismiss) ── */
  const resetScanner = () => {
    if (resultTimeoutRef.current) {
      clearTimeout(resultTimeoutRef.current);
    }
    setScanResult(null);
    setScanError('');
    lastScannedRef.current = '';
  };

  /* ── Logout ── */
  const handleLogout = () => {
    stopScanner();
    setIsAuthenticated(false);
    setApiKey('');
    sessionStorage.removeItem('rca_scan_key');
  };

  /* ══════════════════════════════════════
     RENDER: Auth Screen
     ══════════════════════════════════════ */
  if (!isAuthenticated) {
    return (
      <div className="qs-wrapper">
        <div className="qs-auth-card">
          <div className="qs-auth-icon">
            <FontAwesomeIcon icon={faLock} />
          </div>
          <h1>QR Scanner Access</h1>
          <p>Enter the admin API key to access the QR scanner</p>

          <form onSubmit={handleAuthSubmit} className="qs-auth-form">
            <div className="qs-auth-input-wrap">
              <FontAwesomeIcon icon={faKey} className="input-icon" />
              <input
                type="password"
                placeholder="Enter API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoFocus
              />
            </div>
            {authError && (
              <div className="qs-auth-error">
                <FontAwesomeIcon icon={faXmark} /> {authError}
              </div>
            )}
            <button type="submit" disabled={authLoading} className="qs-auth-btn">
              {authLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <>
                  Access Scanner
                  <FontAwesomeIcon icon={faArrowRight} />
                </>
              )}
            </button>
          </form>

          <div className="qs-auth-footer">
            <p>Contact admin for access credentials</p>
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════
     RENDER: Scanner Screen
     ══════════════════════════════════════ */
  return (
    <div className="qs-wrapper">
      <div className="qs-container">
        {/* ── Header ── */}
        <div className="qs-header">
          <div className="qs-title">
            <FontAwesomeIcon icon={faQrcode} className="qs-icon" />
            <div>
              <h1>QR Scanner</h1>
              <p>Scan entry passes for verification</p>
            </div>
          </div>
          <button className="qs-logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>

        {/* ── Mode Toggle ── */}
        <div className="qs-mode-toggle">
          <button
            className={`qs-mode-btn ${scanMode === 'camera' ? 'active' : ''}`}
            onClick={() => { stopScanner(); setScanMode('camera'); setScanResult(null); }}
          >
            <FontAwesomeIcon icon={faCamera} />
            Camera
          </button>
          <button
            className={`qs-mode-btn ${scanMode === 'upload' ? 'active' : ''}`}
            onClick={() => { stopScanner(); setScanMode('upload'); setScanResult(null); }}
          >
            <FontAwesomeIcon icon={faImage} />
            Upload Image
          </button>
        </div>

        {/* ── Scanner Area ── */}
        <div className="qs-scanner-area">
          {scanMode === 'camera' ? (
            <>
              <div id="qr-reader" className="qs-reader"></div>
              
              {!scanning && (
                <div className="qs-camera-controls">
                  <button className="qs-start-btn" onClick={startScanner}>
                    <FontAwesomeIcon icon={faCamera} />
                    Start Camera
                  </button>
                </div>
              )}

              {scanning && cameras.length > 1 && (
                <button className="qs-switch-btn" onClick={switchCamera}>
                  <FontAwesomeIcon icon={faCameraRotate} />
                </button>
              )}

              {scanning && (
                <button className="qs-stop-btn" onClick={stopScanner}>
                  Stop Camera
                </button>
              )}
            </>
          ) : (
            <>
              <div id="qr-reader-upload" style={{ display: 'none' }}></div>
              <div className="qs-upload-area">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  id="qr-upload-input"
                  hidden
                />
                <label htmlFor="qr-upload-input" className="qs-upload-label">
                  <FontAwesomeIcon icon={faImage} />
                  <span>Click to upload QR code image</span>
                  <small>or drag and drop</small>
                </label>
              </div>
            </>
          )}

          {/* Loading */}
          {scanLoading && (
            <div className="qs-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Processing QR code...</span>
            </div>
          )}

          {/* Error */}
          {scanError && (
            <div className="qs-error">
              <FontAwesomeIcon icon={faXmark} />
              <span>{scanError}</span>
              <button onClick={resetScanner}>Try Again</button>
            </div>
          )}
        </div>

        {/* ── Scan Result (Overlay) ── */}
        {scanResult && (
          <div className={`qs-result ${scanResult.type}`}>
            <div className="qs-result-progress"></div>
            <div className="qs-result-header">
              <div className={`qs-result-icon ${scanResult.type}`}>
                <FontAwesomeIcon icon={scanResult.type === 'success' ? faCheck : faExclamationTriangle} />
              </div>
              <div className="qs-result-title">
                <h2>{scanResult.type === 'success' ? 'Entry Approved' : 'Entry Denied'}</h2>
                <p>{scanResult.message}</p>
              </div>
            </div>

            {scanResult.data && (
              <div className="qs-result-details">
                <div className="qs-detail-row">
                  <FontAwesomeIcon icon={faUser} />
                  <span className="label">Name</span>
                  <span className="value">{scanResult.data.name}</span>
                </div>
                <div className="qs-detail-row">
                  <FontAwesomeIcon icon={faEnvelope} />
                  <span className="label">Email</span>
                  <span className="value">{scanResult.data.email}</span>
                </div>
                <div className="qs-detail-row">
                  <FontAwesomeIcon icon={faPhone} />
                  <span className="label">Phone</span>
                  <span className="value">{scanResult.data.phone}</span>
                </div>
                <div className="qs-detail-row">
                  <FontAwesomeIcon icon={faIdCard} />
                  <span className="label">Roll</span>
                  <span className="value">{scanResult.data.roll}</span>
                </div>
                <div className="qs-detail-row">
                  <FontAwesomeIcon icon={faBuilding} />
                  <span className="label">Hall</span>
                  <span className="value">{scanResult.data.hall}</span>
                </div>
                <div className="qs-detail-row highlight">
                  <FontAwesomeIcon icon={faTicket} />
                  <span className="label">Passes</span>
                  <span className="value">
                    {scanResult.data.scanned_count} / {scanResult.data.passes} used
                    {scanResult.data.remaining !== undefined && (
                      <span className="remaining"> ({scanResult.data.remaining} remaining)</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            <button className="qs-scan-again-btn" onClick={resetScanner}>
              <FontAwesomeIcon icon={faXmark} />
              Dismiss
            </button>
            <p className="qs-auto-dismiss-note">Auto-dismissing in a few seconds...</p>
          </div>
        )}
      </div>
    </div>
  );
}
